import { apiSlice } from './apiSlice';

export interface DirectoryStore {
  id: string;
  orgId: string;
  name: string;
  status: string;
  timezone: string;
  currency: string;
  createdAt: string;
}

export interface DirectoryWorkspace {
  id: string;
  orgId: string;
  storeId: string;
  name: string;
  kind: string;
  status: string;
}

export interface CursorPage<T> {
  data: T[];
  pagination: { cursor: string | null; hasMore: boolean };
}

export interface DirectoryListParams {
  cursor?: string;
  limit?: number;
  search?: string;
  status?: string;
  storeId?: string;
}

interface DataResponse<T> {
  data: T;
}

export const directoryApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getStores: builder.query<CursorPage<DirectoryStore>, DirectoryListParams | void>({
      query: (params) => ({
        url: '/organizations/v1/directory/stores',
        params: params || undefined,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'DirectoryStore' as const, id })),
              { type: 'DirectoryStore', id: 'LIST' },
            ]
          : [{ type: 'DirectoryStore', id: 'LIST' }],
    }),
    getStore: builder.query<DirectoryStore, string>({
      query: (id) => `/organizations/v1/directory/stores/${id}`,
      transformResponse: (response: DataResponse<DirectoryStore>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'DirectoryStore', id }],
    }),
    createStore: builder.mutation<
      DirectoryStore,
      { name: string; timezone: string; currency: string }
    >({
      query: (body) => ({ url: '/organizations/v1/directory/stores', method: 'POST', body }),
      transformResponse: (response: DataResponse<DirectoryStore>) => response.data,
      invalidatesTags: [{ type: 'DirectoryStore', id: 'LIST' }],
    }),
    updateStore: builder.mutation<
      DirectoryStore,
      { id: string; body: { name?: string; timezone?: string; status?: string } }
    >({
      query: ({ id, body }) => ({
        url: `/organizations/v1/directory/stores/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: DataResponse<DirectoryStore>) => response.data,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'DirectoryStore', id },
        { type: 'DirectoryStore', id: 'LIST' },
      ],
    }),
    getWorkspaces: builder.query<CursorPage<DirectoryWorkspace>, DirectoryListParams | void>({
      query: (params) => ({
        url: '/organizations/v1/directory/workspaces',
        params: params || undefined,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'DirectoryWorkspace' as const, id })),
              { type: 'DirectoryWorkspace', id: 'LIST' },
            ]
          : [{ type: 'DirectoryWorkspace', id: 'LIST' }],
    }),
    getWorkspace: builder.query<DirectoryWorkspace, string>({
      query: (id) => `/organizations/v1/directory/workspaces/${id}`,
      transformResponse: (response: DataResponse<DirectoryWorkspace>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'DirectoryWorkspace', id }],
    }),
    createWorkspace: builder.mutation<
      DirectoryWorkspace,
      { storeId: string; name: string; kind: string; status?: string }
    >({
      query: (body) => ({ url: '/organizations/v1/directory/workspaces', method: 'POST', body }),
      transformResponse: (response: DataResponse<DirectoryWorkspace>) => response.data,
      invalidatesTags: [{ type: 'DirectoryWorkspace', id: 'LIST' }],
    }),
    updateWorkspace: builder.mutation<
      DirectoryWorkspace,
      { id: string; body: { name?: string; kind?: string; status?: string } }
    >({
      query: ({ id, body }) => ({
        url: `/organizations/v1/directory/workspaces/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: DataResponse<DirectoryWorkspace>) => response.data,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'DirectoryWorkspace', id },
        { type: 'DirectoryWorkspace', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetStoresQuery,
  useGetStoreQuery,
  useCreateStoreMutation,
  useUpdateStoreMutation,
  useGetWorkspacesQuery,
  useGetWorkspaceQuery,
  useCreateWorkspaceMutation,
  useUpdateWorkspaceMutation,
} = directoryApi;
